import React, { useEffect, useRef } from "react";
import { Card, Grid } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

/**
 * 📅 DayRangeSelector
 * 
 * Displays date range options as selectable cards.
 * Only one can be active at a time (single-select).
 * 
 * Props:
 * - selectedDateRange (string): currently selected range
 * - onChange (function): callback fired when range changes
 */
const DayRangeSelector = ({ selectedDateRange, onChange }) => {
  const initialized = useRef(false);

  // Define your available ranges here
  const ranges = [
    { key: "last7Days", label: "Last 7 Days" },
    { key: "last30Days", label: "Last 30 Days" },
  ];

  // Default selection (first load)
  useEffect(() => {
    if (!initialized.current && !selectedDateRange) {
      onChange("last30Days");
      initialized.current = true;
    }
  }, [selectedDateRange, onChange]);

  // Handle click — activates only one range
  const handleSelect = (key) => {
    if (selectedDateRange !== key) {
      onChange(key);
    }
  };

  return (
    <MDBox>
      <MDTypography
        variant="button"
        fontWeight="medium"
        color="text"
        sx={{ marginBottom: 1, display: "block", textAlign: "center", }}
      >
        Date Range
      </MDTypography>
      <MDBox
        bgColor="#f7f7f7" // light grey background
        sx={{
          
          borderRadius: "16px",
          backgroundColor: "#f0f2f5",
          padding: "14px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
      <Grid container spacing={2}>
        {ranges.map(({ key, label }) => {
          const isSelected = selectedDateRange === key;

          return (
            <Grid item key={key}>
              <Card
                onClick={() => handleSelect(key)}
                sx={{
                  cursor: "pointer",
                  padding: "12px 18px",
                  borderRadius: "12px",
                  minWidth: 140,
                  textAlign: "center",
                  boxShadow: isSelected ? 6 : 1,
                  border: isSelected ? "2px solid #00A1A1" : "1px solid #ddd",
                  backgroundColor: isSelected ? "grey.400" : "grey.100",
                  color: isSelected ? "black" : "text.primary",
                  position: "relative",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                }}
              >
                <MDTypography
                  variant="button"
                  fontWeight={isSelected ? "bold" : "medium"}
                  sx={{ fontSize: "0.9rem" }}
                >
                  {label}
                </MDTypography>

                {/* ✅ Show checkmark if selected */}
                {isSelected && (
                  <MDBox
                    sx={{
                      position: "absolute",
                      top: 3,
                      right: 3,
                      backgroundColor: "#26282b",
                      borderRadius: "50%",
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CheckCircleIcon color="accent" sx={{ fontSize: 18 }} />
                  </MDBox>
                )}
              </Card>
            </Grid>
          );
        })}
      </Grid>
      </MDBox>
    </MDBox>
  );
};

export default DayRangeSelector;
